/**
 * ClipprManager.ts - Core clipboard manager
 * 
 * CHANGE HISTORY:
 * - 251102_1145 [pending] danielbeer: Created core clipboard manager with Actr integration
 * 
 * Main clipboard manager with Actr integration, format detection, history, and slots.
 */

import { ClipboardFormatUtil } from './ClipboardFormat';
import { ClipboardHistory } from './ClipboardHistory';
import { ClipboardSlots } from './ClipboardSlots';
import { FileMakerIntegration } from './FileMakerIntegration';
import { CrossTabSync } from './CrossTabSync';
import { RichFormatSupport } from './RichFormatSupport';
import type {
  ClipboardItem,
  ClipboardOptions,
  PasteOptions,
  ClipprConfig,
  ClipboardListener,
  ClipboardEvent,
  ClipboardStats,
  ClipboardExport,
  ClipboardImportResult,
  FileMakerClipboardItem
} from '../types';

/**
 * Generate unique ID
 */
function generateId(): string {
  return `clip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Core clipboard manager
 */
export class ClipprManager {
  private actr: any;
  private loggr: any;
  private aggregatr?: any;
  private contextr?: any;
  private fileMaker: FileMakerIntegration;
  private crossTabSync?: CrossTabSync;
  
  private clipboard: ClipboardItem | null = null;
  private history: ClipboardHistory;
  private slots: ClipboardSlots;
  private listeners: Set<ClipboardListener> = new Set();
  
  private stats: ClipboardStats = {
    totalCopies: 0,
    totalCuts: 0,
    totalPastes: 0,
    totalClears: 0,
    historySize: 0,
    activeSlots: 0,
    formatDistribution: {}
  };
  
  constructor(config: ClipprConfig = {}) {
    // Get Actr if provided, otherwise try to get it from Dependr
    this.actr = config.actr || this._tryGetActr();
    this.loggr = config.loggr;
    this.aggregatr = config.aggregatr;
    this.contextr = config.contextr;
    this.fileMaker = new FileMakerIntegration();
    
    this.history = new ClipboardHistory(config.maxHistory || 10);
    this.slots = new ClipboardSlots(config.defaultSlot || 'default');
    
    // Initialize cross-tab sync if enabled
    if (config.enableCrossTabs) {
      this.crossTabSync = new CrossTabSync({
        enabled: true,
        syncHistory: true,
        syncSlots: true,
        loggr: this.loggr
      });
      
      // Subscribe to cross-tab messages
      this.crossTabSync.subscribe((message) => {
        this.handleCrossTabMessage(message);
      });
    }
    
    // Register actions with Actr if available
    if (this.actr) {
      this._registerActions();
    }
  }
  
  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      return null;
    }
  }
  
  /**
   * Register clipboard actions with Actr
   */
  private _registerActions(): void {
    // Copy action
    this.actr.register('clippr:copy', {
      handler: async (params: any) => {
        return this.copy(params.data, params.options);
      },
      description: 'Copy data to clipboard'
    });
    
    // Cut action
    this.actr.register('clippr:cut', {
      handler: async (params: any) => {
        return this.cut(params.data, params.options);
      },
      description: 'Cut data to clipboard'
    });
    
    // Paste action
    this.actr.register('clippr:paste', {
      handler: async (params: any) => {
        return this.paste(params.options);
      },
      description: 'Paste from clipboard'
    });
    
    // Clear action
    this.actr.register('clippr:clear', {
      handler: async (params: any) => {
        return this.clear(params.slot);
      },
      description: 'Clear clipboard slot'
    });
    
    // Get history action
    this.actr.register('clippr:get-history', {
      handler: async () => {
        return this.getHistory();
      },
      description: 'Get clipboard history'
    });
    
    // Paste from history action
    this.actr.register('clippr:paste-from-history', {
      handler: async (params: any) => {
        return this.pasteFromHistory(params.index);
      },
      description: 'Paste from clipboard history'
    });
    
    // Get stats action
    this.actr.register('clippr:get-stats', {
      handler: async () => {
        return this.getStats();
      },
      description: 'Get clipboard statistics'
    });
  }
  
  /**
   * Get context-aware slot name
   * Uses Contextr to determine appropriate slot based on current context
   */
  private getContextAwareSlot(providedSlot?: string): string {
    // If slot explicitly provided, use it
    if (providedSlot) {
      return providedSlot;
    }
    
    // If Contextr available, get context-based slot
    if (this.contextr) {
      const context = this.contextr.getCurrentContext();
      if (context?.id) {
        // Use context ID as slot name (e.g., "form:contact-edit", "view:calendar")
        return `context:${context.id}`;
      }
    }
    
    // Fall back to default slot
    return this.slots.getDefaultSlot();
  }
  
  /**
   * Copy data to clipboard
   */
  async copy(data: any, options: ClipboardOptions = {}): Promise<ClipboardItem> {
    // Detect format if not provided
    const formatResult = options.format 
      ? { format: options.format, confidence: 1.0 }
      : ClipboardFormatUtil.detect(data);
    
    // Get context-aware slot
    const slot = this.getContextAwareSlot(options.slot);
    
    // Create clipboard item
    const item: ClipboardItem = {
      id: generateId(),
      data,
      format: formatResult.format,
      metadata: {
        ...options.metadata,
        slot,
        timestamp: Date.now()
      },
      timestamp: Date.now(),
      operation: 'copy'
    };
    
    
    // Store in slot
    this.slots.setItem(slot, item);
    
    // Update main clipboard if default slot
    if (slot === this.slots.getDefaultSlot()) {
      this.clipboard = item;
      this.history.add(item);
    }
    
    // Write to system clipboard
    await this.writeToSystemClipboard(item);
    
    // Persist if requested
    if (options.persist && this.aggregatr) {
      await this.aggregatr.set(`clippr:${item.id}`, item);
    }
    
    // Update stats
    this.stats.totalCopies++;
    this.stats.formatDistribution[item.format] = 
      (this.stats.formatDistribution[item.format] || 0) + 1;
    this.updateStats();
    
    // Notify listeners
    this.notifyListeners({
      type: 'copy',
      item,
      slot,
      timestamp: Date.now()
    });
    
    // Log via Loggr
    if (this.loggr) {
      this.loggr.info('Clipboard copy', { 
        itemId: item.id, 
        slot, 
        format: item.format 
      });
    }
    
    return item;
  }
  
  /**
   * Cut data (copy + mark for deletion)
   */
  async cut(data: any, options: ClipboardOptions = {}): Promise<ClipboardItem> {
    const item = await this.copy(data, options);
    item.operation = 'cut';
    
    // Update stats
    this.stats.totalCuts++;
    this.stats.totalCopies--; // Adjust since copy incremented it
    
    // Notify listeners
    this.notifyListeners({
      type: 'cut',
      item,
      slot: options.slot || this.slots.getDefaultSlot(),
      timestamp: Date.now()
    });
    
    // Log
    if (this.loggr) {
      this.loggr.info('Clipboard cut', { 
        itemId: item.id, 
        slot: options.slot || this.slots.getDefaultSlot()
      });
    }
    
    return item;
  }
  
  /**
   * Paste data from clipboard
   */
  paste(options: PasteOptions = {}): any {
    const slot = this.getContextAwareSlot(options.slot);
    const item = this.slots.getItem(slot);
    
    if (!item) {
      return null;
    }
    
    // Update stats
    this.stats.totalPastes++;
    
    // Notify listeners
    this.notifyListeners({
      type: 'paste',
      item,
      slot,
      timestamp: Date.now()
    });
    
    // Log
    if (this.loggr) {
      this.loggr.info('Clipboard paste', { 
        itemId: item.id, 
        slot,
        consume: options.consume 
      });
    }
    
    // If consume and was cut operation, remove from clipboard
    if (options.consume && item.operation === 'cut') {
      this.slots.clearSlot(slot);
      if (slot === this.slots.getDefaultSlot()) {
        this.clipboard = null;
      }
    }
    
    return item.data;
  }
  
  /**
   * Peek at clipboard without pasting
   */
  peek(slot?: string): any {
    const slotName = this.getContextAwareSlot(slot);
    const item = this.slots.getItem(slotName);
    return item?.data || null;
  }
  
  /**
   * Check if clipboard has data
   */
  hasData(slot?: string): boolean {
    const slotName = this.getContextAwareSlot(slot);
    return this.slots.hasData(slotName);
  }
  
  /**
   * Clear clipboard
   */
  clear(slot?: string): void {
    if (slot === 'all') {
      this.slots.clearAll(true);
      this.clipboard = null;
    } else {
      const slotName = slot || this.slots.getDefaultSlot();
      this.slots.clearSlot(slotName);
      
      if (slotName === this.slots.getDefaultSlot()) {
        this.clipboard = null;
      }
    }
    
    // Update stats
    this.stats.totalClears++;
    
    // Notify listeners
    this.notifyListeners({
      type: 'clear',
      slot,
      timestamp: Date.now()
    });
    
    // Log
    if (this.loggr) {
      this.loggr.info('Clipboard clear', { slot });
    }
  }
  
  /**
   * Get clipboard history
   */
  getHistory(): ClipboardItem[] {
    return this.history.getAll();
  }
  
  /**
   * Paste from history
   */
  pasteFromHistory(index: number): any {
    const item = this.history.get(index);
    
    if (!item) {
      return null;
    }
    
    // Update stats
    this.stats.totalPastes++;
    
    // Notify listeners
    this.notifyListeners({
      type: 'paste',
      item,
      timestamp: Date.now()
    });
    
    // Log
    if (this.loggr) {
      this.loggr.info('Clipboard paste from history', { 
        itemId: item.id, 
        index 
      });
    }
    
    return item.data;
  }
  
  /**
   * Get all slot names
   */
  getSlots(): string[] {
    return this.slots.getSlotNames();
  }
  
  /**
   * Subscribe to clipboard events
   */
  subscribe(listener: ClipboardListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  /**
   * Notify listeners
   */
  private notifyListeners(event: ClipboardEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        if (this.loggr) {
          this.loggr.error('Clipboard listener error', { error });
        }
      }
    });
  }
  
  /**
   * Write to system clipboard
   */
  private async writeToSystemClipboard(item: ClipboardItem): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    
    try {
      // Write text data
      if (ClipboardFormatUtil.isText(item.format)) {
        const text = typeof item.data === 'string' 
          ? item.data 
          : JSON.stringify(item.data);
        await navigator.clipboard.writeText(text);
      }
    } catch (error) {
      if (this.loggr) {
        this.loggr.warn('Failed to write to system clipboard', { error });
      }
    }
  }
  
  /**
   * Get statistics
   */
  getStats(): ClipboardStats {
    return {
      ...this.stats,
      historySize: this.history.size(),
      activeSlots: this.slots.getActiveSlots().length
    };
  }
  
  /**
   * Update internal stats
   */
  private updateStats(): void {
    this.stats.historySize = this.history.size();
    this.stats.activeSlots = this.slots.getActiveSlots().length;
  }
  
  /**
   * Export clipboard data
   */
  export(): ClipboardExport {
    return {
      clipboard: this.clipboard,
      slots: this.slots.export(),
      history: this.history.getAll(),
      stats: this.getStats(),
      exportedAt: Date.now()
    };
  }
  
  /**
   * Import clipboard data
   */
  import(data: ClipboardExport): ClipboardImportResult {
    const errors: string[] = [];
    let itemsImported = 0;
    let slotsImported = 0;
    
    try {
      // Import clipboard
      if (data.clipboard) {
        this.clipboard = data.clipboard;
        itemsImported++;
      }
      
      // Import slots
      if (data.slots) {
        this.slots.import(data.slots);
        slotsImported = data.slots.length;
      }
      
      // Import history
      if (data.history) {
        this.history.import({
          items: data.history,
          maxSize: this.history.getMaxSize()
        });
        itemsImported += data.history.length;
      }
      
      return {
        success: true,
        itemsImported,
        slotsImported
      };
    } catch (error) {
      errors.push((error as Error).message);
      return {
        success: false,
        itemsImported,
        slotsImported,
        errors
      };
    }
  }
  
  // ============================================================================
  // FileMaker Integration Methods
  // ============================================================================
  
  /**
   * Copy FileMaker XML to clipboard
   */
  async copyFileMakerXML(xml: string, options: ClipboardOptions = {}): Promise<ClipboardItem> {
    // Validate FileMaker XML
    if (!this.fileMaker.isValidFileMakerXML(xml)) {
      throw new Error('Invalid FileMaker XML');
    }
    
    // Create clipboard item with FileMaker metadata
    const fmData = this.fileMaker.createClipboardItem(xml);
    
    return this.copy(fmData.data, {
      ...options,
      format: 'application/x-filemaker-xml',
      metadata: {
        ...options.metadata,
        ...fmData.metadata
      }
    });
  }
  
  /**
   * Paste FileMaker XML from clipboard
   */
  pasteFileMakerXML(options: PasteOptions = {}): FileMakerClipboardItem | null {
    const data = this.paste(options);
    
    if (!data || typeof data !== 'string') {
      return null;
    }
    
    if (!this.fileMaker.isValidFileMakerXML(data)) {
      return null;
    }
    
    return this.fileMaker.parseFileMakerXML(data);
  }
  
  /**
   * Check if clipboard contains FileMaker XML
   */
  hasFileMakerXML(slot?: string): boolean {
    const slotName = this.getContextAwareSlot(slot);
    const item = this.slots.getItem(slotName);
    
    if (!item || item.format !== 'application/x-filemaker-xml') {
      return false;
    }
    
    return this.fileMaker.isValidFileMakerXML(item.data);
  }
  
  /**
   * Get FileMaker clipboard item metadata
   */
  getFileMakerMetadata(slot?: string): FileMakerClipboardItem | null {
    const slotName = this.getContextAwareSlot(slot);
    const item = this.slots.getItem(slotName);
    
    if (!item) {
      return null;
    }
    
    return this.fileMaker.extractFileMakerData(item);
  }
  
  // ============================================================================
  // Cross-Tab Sync Methods
  // ============================================================================
  
  /**
   * Handle cross-tab sync message
   */
  private handleCrossTabMessage(message: any): void {
    if (!message) return;
    
    try {
      switch (message.type) {
        case 'copy':
        case 'cut':
          if (message.item && message.slot) {
            // Update slot from other tab
            this.slots.setItem(message.slot, message.item);
            this.notifyListeners({
              type: 'system-sync',
              item: message.item,
              slot: message.slot,
              timestamp: Date.now()
            });
          }
          break;
          
        case 'clear':
          if (message.slot) {
            this.slots.clearSlot(message.slot);
            this.notifyListeners({
              type: 'system-sync',
              slot: message.slot,
              timestamp: Date.now()
            });
          }
          break;
          
        case 'history-sync':
          if (message.items && Array.isArray(message.items)) {
            // Merge history from other tab
            message.items.forEach((item: any) => {
              this.history.add(item);
            });
          }
          break;
          
        case 'slot-sync':
          if (message.slot) {
            if (message.item) {
              this.slots.setItem(message.slot, message.item);
            } else {
              this.slots.clearSlot(message.slot);
            }
          }
          break;
      }
    } catch (error) {
      if (this.loggr) {
        this.loggr.error('Error handling cross-tab message', { error });
      }
    }
  }
  
  /**
   * Check if cross-tab sync is enabled
   */
  isCrossTabSyncEnabled(): boolean {
    return this.crossTabSync?.isEnabled() ?? false;
  }
}

// Singleton instance
let instance: ClipprManager | null = null;

/**
 * Get singleton Clippr instance
 */
export function getClipr(config?: ClipprConfig): ClipprManager {
  if (!instance) {
    instance = new ClipprManager(config);
  }
  return instance;
}
